import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronRight, Menu, X, Network, ShieldCheck, Code2, Layers, Cpu, Building2 } from "lucide-react";
import { SXLogo } from "../ui";
import { SOLUTIONS, SEGMENTS } from "./sxData";
import { ThemeToggle } from "../../lib/theme";

const p = (slug, name) => [`/products/${slug}`, name];
export const MENU = [
  ["About Us", "/company"],
  ["Products", [
    [Network, "Payments", "Switching, routing and reconciliation for every transaction.", [p("upi-switch", "UPI Switch"), p("intelligent-routing", "Intelligent Routing"), p("reconciliation", "Reconciliation & Settlement")]],
    [ShieldCheck, "Onboarding & Risk", "Open accounts, onboard merchants and watch every signal.", [p("onboarding", "Account & Merchant Onboarding"), p("risk-monitoring", "Risk & Monitoring")]],
    [Code2, "Developers", "APIs, webhooks and a sandbox that never moves money.", [p("developer-platform", "Developer Platform"), ["/developers", "Developer portal"]]],
    [Layers, "Platform", "The architecture, integrations and security behind it.", [["/platform", "Architecture"], ["/integrations", "Integrations"], ["/security", "Security"]]],
  ]],
  ["Solutions", [
    [Layers, "Payments", "Run, route and reconcile every payment.", SOLUTIONS.slice(0, 1).concat(SOLUTIONS.slice(3, 5)).map((s) => [`/solutions/${s.slug}`, s.name])],
    [Cpu, "Onboarding & Partners", "Bring merchants, customers and partners on board.", [SOLUTIONS[1], SOLUTIONS[2], SOLUTIONS[5]].map((s) => [`/solutions/${s.slug}`, s.name]).concat([["/solutions", "All solutions"]])],
  ]],
  ["Who We Serve", [
    [Building2, "Banks", "Configured for how Indian banks actually operate.", SEGMENTS.filter((s) => s.group === "Banks").map((s) => [`/who-we-serve/${s.slug}`, s.name])],
    [Network, "Fintechs & PSPs", "Build on a developer-first, health-aware switch.", SEGMENTS.filter((s) => s.group !== "Banks").map((s) => [`/who-we-serve/${s.slug}`, s.name]).concat([["/who-we-serve", "Everyone we serve"]])],
  ]],
  ["Developers", "/developers"],
  ["Contact Us", "/contact"],
];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Mega({ groups, name, onClose }) {
  const [g, setG] = useState(0);
  const [I, title, desc, links] = groups[g];
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }}
      className="absolute left-1/2 top-full mt-3 grid w-[620px] -translate-x-1/2 grid-cols-[200px_1fr] gap-3 rounded-3xl border border-white/10 bg-[#0B1730]/95 p-3 shadow-2xl shadow-black/50 backdrop-blur-xl" data-testid={`mega-${slug(name)}`}>
      <div className="rounded-2xl bg-gradient-to-b from-transparent to-[#246BFD]/20 p-1.5">
        {groups.map(([GI, t], k) => (
          <button key={t} onMouseEnter={() => setG(k)} onFocus={() => setG(k)} data-testid={`mega-group-${slug(t)}`}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${g === k ? "bg-[#246BFD]/25 text-white" : "text-[#98A8C0] hover:text-white"}`}>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-[#31D7FF]/30 bg-[#07111F]"><GI className="h-4 w-4 text-[#31D7FF]" /></span>{t}</button>))}
      </div>
      <div className="border-l border-white/10 pl-3">
        <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-4"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#246BFD]/20"><I className="h-5 w-5 text-[#31D7FF]" /></span>
          <div><div className="font-semibold text-white">{title}</div><div className="mt-0.5 text-xs text-[#98A8C0]">{desc}</div></div></div>
        <div className="mt-2 py-1">{links.map(([to, l]) => (
          <Link key={l} to={to} onClick={onClose} data-testid={`mega-link-${slug(l)}`} className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#cfe0f5] transition-colors hover:bg-white/5 hover:text-[#31D7FF]">
            <ChevronRight className="h-3.5 w-3.5 text-[#98A8C0] transition-transform group-hover:translate-x-0.5" />{l}</Link>))}</div>
      </div>
    </motion.div>
  );
}

function DesktopNav() {
  const [open, setOpen] = useState(null);
  const t = useRef();
  const enter = (n) => { clearTimeout(t.current); setOpen(n); };
  const leave = () => { t.current = setTimeout(() => setOpen(null), 150); };
  const link = ({ isActive }) => `rounded-full px-3 py-2 text-sm transition-colors ${isActive ? "text-white" : "text-[#98A8C0] hover:text-white"}`;
  return (
    <nav className="hidden flex-1 items-center justify-center gap-0.5 lg:flex" onKeyDown={(e) => e.key === "Escape" && setOpen(null)}>
      {MENU.map(([n, v]) => typeof v === "string" ? <NavLink key={n} to={v} className={link} data-testid={`site-nav-${slug(n)}`}>{n}</NavLink> : (
        <div key={n} className="relative" onMouseEnter={() => enter(n)} onMouseLeave={leave}>
          <button onClick={() => setOpen(open === n ? null : n)} aria-expanded={open === n} data-testid={`site-nav-${slug(n)}`}
            className={`flex items-center gap-1 rounded-full px-3 py-2 text-sm transition-colors ${open === n ? "text-white" : "text-[#98A8C0] hover:text-white"}`}>
            {n}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === n ? "rotate-180" : ""}`} /></button>
          <AnimatePresence>{open === n && <Mega groups={v} name={n} onClose={() => setOpen(null)} />}</AnimatePresence>
        </div>))}
    </nav>
  );
}

function MobileNav({ onClose }) {
  const [sec, setSec] = useState(null);
  const row = "block rounded-xl px-3 py-2.5 text-[#cfe0f5]";
  return (
    <motion.nav initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-2 max-h-[75vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0B1730]/95 p-3 backdrop-blur-xl lg:hidden" data-testid="mobile-nav">
      {MENU.map(([n, v]) => typeof v === "string" ? <Link key={n} to={v} onClick={onClose} className={row} data-testid={`mnav-${slug(n)}`}>{n}</Link> : (
        <div key={n}>
          <button onClick={() => setSec(sec === n ? null : n)} className={`${row} flex w-full items-center justify-between`} data-testid={`mnav-${slug(n)}`}>{n}<ChevronDown className={`h-4 w-4 transition-transform ${sec === n ? "rotate-180" : ""}`} /></button>
          {sec === n && <div className="mb-2 ml-3 border-l border-white/10 pl-3">{v.map(([, t, , links]) => (
            <div key={t} className="py-1"><div className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-[#31D7FF]">{t}</div>
              {links.map(([to, l]) => <Link key={l} to={to} onClick={onClose} className="block px-2 py-1.5 text-sm text-[#98A8C0]">{l}</Link>)}</div>))}</div>}
        </div>))}
      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
        <Link to="/login" onClick={onClose} className="rounded-full border border-white/15 py-2.5 text-center text-sm" data-testid="mnav-sign-in">Sign in</Link>
        <Link to="/contact?type=demo" onClick={onClose} className="rounded-full bg-[#246BFD] py-2.5 text-center text-sm font-medium" data-testid="mnav-request-a-demo">Request a Demo</Link>
      </div>
    </motion.nav>
  );
}

export function SiteHeader({ theme, toggle }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 10);
    f(); window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className="pointer-events-none sticky top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-4" data-testid="site-header">
      <div className={`pointer-events-auto mx-auto max-w-6xl rounded-full border px-4 transition-[background-color,box-shadow,border-color] duration-300 sm:px-5 ${scrolled ? "border-white/10 bg-[#0B1730]/85 shadow-2xl shadow-black/40 backdrop-blur-xl" : "border-white/10 bg-[#0B1730]/60 backdrop-blur-md"}`}>
        <div className="flex h-14 items-center gap-4 sm:h-16">
          <Link to="/" data-testid="site-logo" className="shrink-0"><SXLogo size={30} /></Link>
          <DesktopNav />
          <div className="ml-auto hidden items-center gap-2 lg:flex">
            <ThemeToggle theme={theme} toggle={toggle} className="border-white/10 text-[#98A8C0] hover:text-white" />
            <Link to="/login" data-testid="site-login" className="rounded-full px-3 py-2 text-sm text-[#98A8C0] transition-colors hover:text-white">Sign in</Link>
            <Link to="/contact?type=demo" data-testid="site-demo-cta" className="rounded-full bg-[#246BFD] px-5 py-2 text-sm font-medium text-white shadow-lg shadow-blue-600/30 transition-colors hover:bg-[#1f5ee0]">Request a Demo</Link>
          </div>
          <ThemeToggle theme={theme} toggle={toggle} testid="theme-toggle-mobile" className="ml-auto border-white/10 text-[#98A8C0] lg:hidden" />
          <button className="grid h-10 w-10 place-items-center rounded-full border border-white/10 lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="site-menu-btn">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>
      <div className="pointer-events-auto mx-auto max-w-6xl"><AnimatePresence>{open && <MobileNav onClose={() => setOpen(false)} />}</AnimatePresence></div>
    </header>
  );
}
